/**
 * Small dense linear algebra, sized for origami vertex-constraint systems
 * (tens to a few hundred rows). Everything is row-major Float64Array based.
 *
 * Nothing here is cache-blocked or BLAS-fast; it is written to be readable and
 * numerically defensible. If the solver ever needs thousands of constraints,
 * swap this module for a sparse implementation - the interfaces are narrow.
 */

export interface Matrix {
  rows: number;
  cols: number;
  data: Float64Array; // row-major, length rows*cols
}

export function zeros(rows: number, cols: number): Matrix {
  return { rows, cols, data: new Float64Array(rows * cols) };
}

export function identity(n: number): Matrix {
  const m = zeros(n, n);
  for (let i = 0; i < n; i++) m.data[i * n + i] = 1;
  return m;
}

export const at = (m: Matrix, r: number, c: number): number => m.data[r * m.cols + c];
export const set = (m: Matrix, r: number, c: number, v: number): void => {
  m.data[r * m.cols + c] = v;
};

/** C = A * B */
export function matmul(a: Matrix, b: Matrix): Matrix {
  if (a.cols !== b.rows) throw new Error(`matmul shape mismatch ${a.cols} vs ${b.rows}`);
  const out = zeros(a.rows, b.cols);
  for (let i = 0; i < a.rows; i++) {
    for (let k = 0; k < a.cols; k++) {
      const aik = a.data[i * a.cols + k];
      if (aik === 0) continue;
      for (let j = 0; j < b.cols; j++) {
        out.data[i * b.cols + j] += aik * b.data[k * b.cols + j];
      }
    }
  }
  return out;
}

/** y = A * x */
export function matvec(a: Matrix, x: Float64Array): Float64Array {
  const y = new Float64Array(a.rows);
  for (let i = 0; i < a.rows; i++) {
    let s = 0;
    const off = i * a.cols;
    for (let j = 0; j < a.cols; j++) s += a.data[off + j] * x[j];
    y[i] = s;
  }
  return y;
}

/** y = A^T * x */
export function matTvec(a: Matrix, x: Float64Array): Float64Array {
  const y = new Float64Array(a.cols);
  for (let i = 0; i < a.rows; i++) {
    const xi = x[i];
    if (xi === 0) continue;
    const off = i * a.cols;
    for (let j = 0; j < a.cols; j++) y[j] += a.data[off + j] * xi;
  }
  return y;
}

export function transpose(a: Matrix): Matrix {
  const out = zeros(a.cols, a.rows);
  for (let i = 0; i < a.rows; i++) {
    for (let j = 0; j < a.cols; j++) out.data[j * a.rows + i] = a.data[i * a.cols + j];
  }
  return out;
}

/**
 * Solve the symmetric positive-definite system (A + lambda*I) x = b by
 * Cholesky factorisation, retrying with a larger ridge if the factorisation
 * hits a non-positive pivot. Returns null if it cannot be regularised.
 */
export function solveSPD(
  a: Matrix,
  b: Float64Array,
  lambda = 0,
  maxRetries = 8,
): Float64Array | null {
  const n = a.rows;
  if (a.cols !== n) throw new Error('solveSPD requires a square matrix');
  let ridge = lambda;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const L = new Float64Array(n * n);
    let ok = true;
    for (let i = 0; i < n && ok; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = a.data[i * n + j] + (i === j ? ridge : 0);
        for (let k = 0; k < j; k++) sum -= L[i * n + k] * L[j * n + k];
        if (i === j) {
          if (!(sum > 0) || !Number.isFinite(sum)) {
            ok = false;
            break;
          }
          L[i * n + i] = Math.sqrt(sum);
        } else {
          L[i * n + j] = sum / L[j * n + j];
        }
      }
    }
    if (ok) {
      // Forward then back substitution.
      const y = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        let s = b[i];
        for (let k = 0; k < i; k++) s -= L[i * n + k] * y[k];
        y[i] = s / L[i * n + i];
      }
      const x = new Float64Array(n);
      for (let i = n - 1; i >= 0; i--) {
        let s = y[i];
        for (let k = i + 1; k < n; k++) s -= L[k * n + i] * x[k];
        x[i] = s / L[i * n + i];
      }
      if (x.every(Number.isFinite)) return x;
      ok = false;
    }
    ridge = ridge === 0 ? 1e-10 : ridge * 100;
  }
  return null;
}

/**
 * Singular values of A, largest first, via one-sided Jacobi rotations on the
 * columns of A. One-sided Jacobi is backward stable and delivers small singular
 * values to high relative accuracy - which matters here because the whole point
 * is to distinguish a genuine zero singular value (a folding mode) from a very
 * small one.
 *
 * Reference: Demmel & Veselic, "Jacobi's method is more accurate than QR",
 * SIAM J. Matrix Anal. Appl. 13(4), 1992.
 */
export function singularValues(a: Matrix, maxSweeps = 60): Float64Array {
  // Work on the tall version: Jacobi orthogonalises columns, so we need rows >= cols.
  const work = a.rows >= a.cols ? a : transpose(a);
  const m = work.rows;
  const n = work.cols;
  const u = Float64Array.from(work.data); // m x n, row-major

  const colDot = (p: number, q: number): number => {
    let s = 0;
    for (let i = 0; i < m; i++) s += u[i * n + p] * u[i * n + q];
    return s;
  };

  const tol = 1e-15;
  for (let sweep = 0; sweep < maxSweeps; sweep++) {
    let off = 0;
    for (let p = 0; p < n - 1; p++) {
      for (let q = p + 1; q < n; q++) {
        const alpha = colDot(p, p);
        const beta = colDot(q, q);
        const gamma = colDot(p, q);
        if (gamma === 0) continue;
        const denom = Math.sqrt(alpha * beta);
        if (denom === 0) continue;
        off = Math.max(off, Math.abs(gamma) / denom);
        if (Math.abs(gamma) / denom < tol) continue;

        const zeta = (beta - alpha) / (2 * gamma);
        const t =
          Math.sign(zeta || 1) / (Math.abs(zeta) + Math.sqrt(1 + zeta * zeta));
        const c = 1 / Math.sqrt(1 + t * t);
        const s = c * t;
        for (let i = 0; i < m; i++) {
          const up = u[i * n + p];
          const uq = u[i * n + q];
          u[i * n + p] = c * up - s * uq;
          u[i * n + q] = s * up + c * uq;
        }
      }
    }
    if (off < tol) break;
  }

  const sv = new Float64Array(n);
  for (let j = 0; j < n; j++) sv[j] = Math.sqrt(Math.max(0, colDot(j, j)));
  sv.sort();
  return sv.reverse();
}

/**
 * Numerical rank: count of singular values above max(sv) * relTol.
 * Also returns the singular values so callers can show the spectrum.
 */
export function numericalRank(
  a: Matrix,
  relTol: number,
): { rank: number; singularValues: Float64Array; conditionNumber: number } {
  if (a.rows === 0 || a.cols === 0) {
    return { rank: 0, singularValues: new Float64Array(0), conditionNumber: 0 };
  }
  const sv = singularValues(a);
  const smax = sv[0] ?? 0;
  const threshold = smax * relTol;
  let rank = 0;
  for (const s of sv) if (s > threshold) rank++;
  const smin = sv[sv.length - 1] ?? 0;
  return {
    rank,
    singularValues: sv,
    conditionNumber: smin > 0 ? smax / smin : Infinity,
  };
}

export const norm2 = (x: Float64Array): number => {
  let s = 0;
  for (let i = 0; i < x.length; i++) s += x[i] * x[i];
  return Math.sqrt(s);
};
